import { mapToApi, mapFromApi } from '@pperform/pp-data-mappers/rating';
import { Rate } from 'interfaces/Rate.interface';
import { ErrorCodes } from 'constants/errors.constants';
import APIError from '../exceptions/APIError';
import AppError from '../exceptions/AppError';
import {
  getRateByShipmentID,
  postRate,
  putRate,
} from '../fetchers/rating.fetchers';
import { checkIsPositiveRating } from '../utils/shipmentRating';

const allRates: Map<number, Rate> = new Map();

export const generateDefaultRate = (
  shipmentId: number,
  trackingExpId: number,
  organizationId: number,
) => ({
  shipmentId,
  trackingExpId,
  organizationId,
  value: 0,
});

export const fetchRateByShipmentID = async (
  shipmentId: number,
  defaultRate: Rate,
): Promise<Rate> => {
  const cachedRate = allRates.get(shipmentId);
  if (cachedRate?.value > 0) {
    return cachedRate;
  }
  let rate: Rate = defaultRate;
  try {
    const { data } = await getRateByShipmentID(shipmentId);
    rate = mapFromApi(data);
  } catch (error) {
    if (error instanceof APIError) {
      throw new AppError('RATING_BY_SHIPMENT_NOT_FOUND');
    }
    throw error;
  } finally {
    allRates.set(shipmentId, rate);
  }
  return allRates.get(shipmentId);
};

export const updateRate = async (request: Rate): Promise<Rate> => {
  let rate: Rate = request;
  try {
    const { data } = await putRate(mapToApi(request));
    rate = mapFromApi(data);
  } catch (error) {
    if (error instanceof APIError) {
      throw new AppError('RATING_BY_SHIPMENT_FAIL');
    }
    throw error;
  } finally {
    allRates.set(rate.shipmentId, rate);
  }
  return allRates.get(rate.shipmentId);
};

export const createRate = async (request: Rate): Promise<Rate> => {
  let rate: Rate = request;
  try {
    const { data } = await postRate(mapToApi(request));
    rate = mapFromApi(data);
  } catch (error) {
    if (error instanceof APIError) {
      throw new AppError('RATING_BY_SHIPMENT_FAIL');
    }
    throw error;
  } finally {
    allRates.set(rate.shipmentId, rate);
  }
  return allRates.get(rate.shipmentId);
};

export const handleRatingForShipment = (
  newRatingValue: number,
  currentRate: Rate,
): Promise<Rate> => {
  if (newRatingValue === currentRate?.value) {
    return Promise.resolve(currentRate);
  }
  const isSameSatisfactionLevel =
    checkIsPositiveRating(newRatingValue) ===
    checkIsPositiveRating(currentRate?.value);
  return currentRate?.value > 0
    ? updateRate({
        ...currentRate,
        value: newRatingValue,
        categories: isSameSatisfactionLevel ? currentRate.categories : [],
        comment: isSameSatisfactionLevel ? currentRate.comment : undefined,
      })
    : createRate({ ...currentRate, value: newRatingValue });
};
